<template>
  <div class="asset-repository h-full min-h-0 overflow-hidden flex flex-col bg-slate-50">
    <!-- 顶部工具栏 -->
    <div class="toolbar flex items-center gap-4 px-5 py-4 bg-white border-b border-slate-200/80 shrink-0">
      <!-- 搜索筛选区 -->
      <div class="flex items-center gap-2.5 flex-1">
        <div class="relative">
          <el-input
            v-model="query.keyword"
            placeholder="搜索素材名称、描述..."
            class="!w-64 !h-8"
            clearable
            @keyup.enter="handleSearch"
            @clear="handleSearch"
          >
            <template #prefix>
              <el-icon class="text-slate-400"><Search /></el-icon>
            </template>
          </el-input>
        </div>
        <el-select
          v-model="query.type"
          placeholder="素材类型"
          class="!w-32 !h-8"
          clearable
          @change="handleSearch"
        >
          <el-option label="全部类型" value="" />
          <el-option label="图片" value="IMAGE" />
          <el-option label="视频" value="VIDEO" />
          <el-option label="文件" value="FILE" />
          <el-option label="文本" value="TEXT" />
        </el-select>
        <el-select
          v-model="query.tagIds"
          placeholder="标签筛选"
          class="!w-40 !h-8"
          multiple
          clearable
          collapse-tags
          collapse-tags-tooltip
          @change="handleSearch"
        >
          <el-option
            v-for="tag in tagList"
            :key="tag.id"
            :label="tag.name"
            :value="tag.id"
          />
        </el-select>
        <el-segmented
          v-if="query.tagIds.length > 1"
          v-model="query.tagMatchMode"
          :options="tagMatchModeOptions"
          size="small"
          @change="handleSearch"
        />
      </div>

      <!-- 分隔线 -->
      <div class="w-px h-6 bg-slate-200/60 shrink-0"></div>

      <!-- 操作按钮区 -->
      <div class="flex items-center gap-1.5 shrink-0">
        <el-button
          v-if="selectedFolder && folderManageable"
          type="primary"
          size="small"
          class="!h-8"
          @click="showUploadDialog = true"
        >
          <el-icon class="mr-1"><Upload /></el-icon>
          上传
        </el-button>
        <el-button
          v-if="selectedFolder && folderManageable"
          size="small"
          class="!h-8"
          @click="showTextCreateDialog = true"
        >
          <el-icon class="mr-1"><Edit /></el-icon>
          新建文本
        </el-button>
        <el-button
          v-if="folderManageable"
          size="small"
          class="!h-8"
          @click="showFolderCreateDialog(selectedFolder)"
        >
          <el-icon class="mr-1"><FolderAdd /></el-icon>
          新建文件夹
        </el-button>
        <el-button
          v-if="folderManageable"
          size="small"
          class="!h-8"
          @click="showTagManageDialog = true"
        >
          <el-icon class="mr-1"><PriceTag /></el-icon>
          标签管理
        </el-button>
      </div>
    </div>

    <!-- 三栏主体 -->
    <div class="main-body flex flex-1 min-h-0 bg-slate-50/30">
      <!-- 左侧：文件夹树 -->
      <AssetFolderTree
        :tree-data="folderTree"
        :selected-id="selectedFolderId"
        :loading="folderLoading"
        class="shrink-0"
        @select="handleFolderSelect"
        @refresh="fetchFolderTree"
        @create-root="showFolderCreateDialog(null)"
        @edit="handleFolderEdit"
        @move="handleFolderMove"
        @acl="handleFolderAcl"
        @delete="handleFolderDelete"
      />

      <!-- 中间：素材网格 -->
      <div class="center-content flex-1 flex flex-col min-w-0 border-r border-slate-200/60 bg-white">
        <AssetGrid
          :list="assetList"
          :loading="assetLoading"
          :selected-id="selectedAssetId"
          :total="pagination.total"
          :current="pagination.current"
          :size="pagination.size"
          :sort-field="query.sortField"
          :sort-order="query.sortOrder"
          @select="handleAssetSelect"
          @page-change="handlePageChange"
          @size-change="handleSizeChange"
          @sort-change="handleSortChange"
        />
      </div>

      <!-- 右侧：详情面板 -->
      <AssetDetailPanel
        v-if="selectedAsset"
        :asset="selectedAsset"
        :versions="versionList"
        :versions-loading="versionLoading"
        :folder-manageable="currentAssetFolderManageable"
        @close="handleDetailClose"
        @edit="handleAssetEdit"
        @delete="handleAssetDelete"
        @move="handleAssetMove"
        @tags="handleAssetTagsEdit"
        @new-version="handleNewVersion"
        @restore-version="handleRestoreVersion"
        @refresh="fetchAssetDetail"
      />
    </div>

    <!-- 上传对话框 -->
    <AssetUploadDialog
      v-model:visible="showUploadDialog"
      :folder-id="selectedFolderId"
      :tag-list="tagList"
      @success="onUploadSuccess"
    />

    <!-- 新建文本对话框 -->
    <AssetTextCreateDialog
      v-model:visible="showTextCreateDialog"
      :folder-id="selectedFolderId"
      :tag-list="tagList"
      @success="onCreateSuccess"
    />

    <!-- 文件夹创建/编辑对话框 -->
    <AssetFolderDialog
      v-model:visible="folderDialog.visible"
      :mode="folderDialog.mode"
      :folder="folderDialog.folder"
      :parent-id="folderDialog.parentId"
      @success="fetchFolderTree"
    />

    <!-- 文件夹移动对话框 -->
    <AssetMoveDialog
      v-model:visible="folderMoveDialog.visible"
      title="移动文件夹"
      :item-name="folderMoveDialog.folderName"
      :tree-data="folderTree"
      :exclude-id="folderMoveDialog.folderId"
      @confirm="handleFolderMoveConfirm"
    />

    <!-- 文件夹ACL对话框 -->
    <AssetFolderAclDialog
      v-model:visible="folderAclDialog.visible"
      :folder-id="folderAclDialog.folderId"
      :folder-name="folderAclDialog.folderName"
      :folder="folderAclDialog.folder"
      @success="fetchFolderTree"
    />

    <!-- 素材编辑对话框 -->
    <AssetEditDialog
      v-model:visible="assetEditDialog.visible"
      :asset="assetEditDialog.asset"
      @success="onAssetUpdateSuccess"
    />

    <!-- 素材移动对话框 -->
    <AssetMoveDialog
      v-model:visible="assetMoveDialog.visible"
      title="移动素材"
      :item-name="assetMoveDialog.assetName"
      :tree-data="folderTree"
      @confirm="handleAssetMoveConfirm"
    />

    <!-- 素材标签编辑对话框 -->
    <AssetTagsEditDialog
      v-model:visible="assetTagsDialog.visible"
      :asset="assetTagsDialog.asset"
      :tag-list="tagList"
      @success="onAssetUpdateSuccess"
    />

    <!-- 标签管理对话框 -->
    <AssetTagManageDialog
      v-model:visible="showTagManageDialog"
      @refresh="fetchTags"
    />

    <!-- 新增版本上传对话框 -->
    <AssetUploadDialog
      v-model:visible="newVersionDialog.visible"
      :folder-id="selectedFolderId"
      :asset-id="newVersionDialog.assetId"
      :tag-list="tagList"
      @success="onVersionUploadSuccess"
    />

    <!-- 新增文本版本对话框 -->
    <AssetTextVersionDialog
      v-model:visible="textVersionDialog.visible"
      :asset="textVersionDialog.asset"
      @success="onVersionUploadSuccess"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Upload, Edit, FolderAdd, PriceTag } from '@element-plus/icons-vue'
import {
  getFolderTree,
  getTagList,
  getAssetPage,
  getAssetDetail,
  getVersionList,
  deleteAsset,
  deleteFolder,
  moveFolder as moveFolderApi,
  moveAsset as moveAssetApi,
  restoreVersion as restoreVersionApi
} from '@/api/assets'

import AssetFolderTree from './components/AssetFolderTree.vue'
import AssetGrid from './components/AssetGrid.vue'
import AssetDetailPanel from './components/AssetDetailPanel.vue'
import AssetUploadDialog from './components/AssetUploadDialog.vue'
import AssetTextCreateDialog from './components/AssetTextCreateDialog.vue'
import AssetFolderDialog from './components/AssetFolderDialog.vue'
import AssetMoveDialog from './components/AssetMoveDialog.vue'
import AssetFolderAclDialog from './components/AssetFolderAclDialog.vue'
import AssetEditDialog from './components/AssetEditDialog.vue'
import AssetTagsEditDialog from './components/AssetTagsEditDialog.vue'
import AssetTagManageDialog from './components/AssetTagManageDialog.vue'
import AssetTextVersionDialog from './components/AssetTextVersionDialog.vue'

// 查询条件
const query = reactive({
  keyword: '',
  type: '',
  tagIds: [],
  tagMatchMode: 'ANY',
  sortField: 'createTime',
  sortOrder: 'DESC'
})

const tagMatchModeOptions = [
  { label: '任一标签', value: 'ANY' },
  { label: '全部标签', value: 'ALL' }
]

// 分页
const pagination = reactive({
  current: 1,
  size: 20,
  total: 0
})

// 文件夹
const folderTree = ref([])
const folderLoading = ref(false)
const selectedFolderId = ref(null)
const selectedFolder = ref(null)

// 计算当前文件夹是否可管理
const folderManageable = computed(() => {
  if (!selectedFolder.value) return false
  return isManageableFolder(selectedFolder.value)
})

// 素材列表
const assetList = ref([])
const assetLoading = ref(false)

// 选中素材
const selectedAssetId = ref(null)
const selectedAsset = ref(null)
const versionList = ref([])
const versionLoading = ref(false)

// 当前选中素材所在文件夹是否可管理
const currentAssetFolderManageable = ref(false)

// 标签
const tagList = ref([])

// 对话框状态
const showUploadDialog = ref(false)
const showTextCreateDialog = ref(false)
const showTagManageDialog = ref(false)

const folderDialog = reactive({ visible: false, mode: 'create', folder: null, parentId: 0 })
const folderMoveDialog = reactive({ visible: false, folderId: null, folderName: '', lockVersion: null })
const folderAclDialog = reactive({ visible: false, folderId: null, folderName: '', folder: null })

const assetEditDialog = reactive({ visible: false, asset: null })
const assetMoveDialog = reactive({ visible: false, assetId: null, assetName: '', lockVersion: null })
const assetTagsDialog = reactive({ visible: false, asset: null })

const newVersionDialog = reactive({ visible: false, assetId: null })
const textVersionDialog = reactive({ visible: false, asset: null })

// ========== 数据加载 ==========

async function fetchFolderTree() {
  folderLoading.value = true
  try {
    const data = await getFolderTree()
    folderTree.value = data || []
  } catch {
    // handled by interceptor
  } finally {
    folderLoading.value = false
  }
}

async function fetchTags() {
  try {
    const data = await getTagList()
    tagList.value = data || []
  } catch {
    // handled by interceptor
  }
}

async function fetchAssetList() {
  assetLoading.value = true
  try {
    const params = {
      page: pagination.current,
      size: pagination.size,
      keyword: query.keyword || undefined,
      type: query.type || undefined,
      tagIds: query.tagIds?.length ? query.tagIds : undefined,
      tagMatchMode: query.tagIds?.length ? query.tagMatchMode : undefined,
      folderId: selectedFolderId.value,
      includeChildren: true,
      sortField: query.sortField,
      sortOrder: query.sortOrder
    }
    const data = await getAssetPage(params)
    assetList.value = data?.records || data?.list || []
    pagination.total = data?.total || 0
  } catch {
    // handled by interceptor
  } finally {
    assetLoading.value = false
  }
}

async function fetchAssetDetail() {
  if (!selectedAssetId.value) return
  try {
    const data = await getAssetDetail(selectedAssetId.value)
    selectedAsset.value = data?.asset || data
    // 判断当前素材文件夹权限
    if (selectedAsset.value?.folderId) {
      const folder = findFolderById(folderTree.value, selectedAsset.value.folderId)
      currentAssetFolderManageable.value = isManageableFolder(folder)
    } else {
      currentAssetFolderManageable.value = false
    }
    await fetchVersionList()
  } catch {
    selectedAsset.value = null
  }
}

function findFolderById(tree, id) {
  for (const node of tree) {
    if (node.id === id) return node
    if (node.children?.length) {
      const found = findFolderById(node.children, id)
      if (found) return found
    }
  }
  return null
}

function isManageableFolder(folder) {
  return folder?.permissionLevel === 'MANAGE'
}

async function fetchVersionList() {
  if (!selectedAssetId.value) return
  versionLoading.value = true
  try {
    const data = await getVersionList(selectedAssetId.value)
    versionList.value = data || []
  } catch {
    // handled by interceptor
  } finally {
    versionLoading.value = false
  }
}

// ========== 事件处理 ==========

function handleSearch() {
  pagination.current = 1
  fetchAssetList()
}

function handleFolderSelect(folder) {
  selectedFolderId.value = folder?.id || null
  selectedFolder.value = folder
  pagination.current = 1
  fetchAssetList()
}

function handleAssetSelect(asset) {
  selectedAssetId.value = asset?.id || null
  if (asset) {
    fetchAssetDetail()
  } else {
    selectedAsset.value = null
    versionList.value = []
  }
}

function handleDetailClose() {
  selectedAssetId.value = null
  selectedAsset.value = null
  versionList.value = []
}

function handlePageChange(page) {
  pagination.current = page
  fetchAssetList()
}

function handleSizeChange(size) {
  pagination.size = size
  pagination.current = 1
  fetchAssetList()
}

function handleSortChange({ field, order }) {
  const nextField = field || 'createTime'
  if (query.sortField === nextField) {
    query.sortOrder = order || (query.sortOrder === 'ASC' ? 'DESC' : 'ASC')
  } else {
    query.sortField = nextField
    query.sortOrder = order || 'DESC'
  }
  pagination.current = 1
  fetchAssetList()
}

// ========== 文件夹操作 ==========

function showFolderCreateDialog(parentFolder) {
  folderDialog.mode = 'create'
  folderDialog.folder = null
  folderDialog.parentId = parentFolder?.id || 0
  folderDialog.visible = true
}

function handleFolderEdit(folder) {
  folderDialog.mode = 'edit'
  folderDialog.folder = folder
  folderDialog.parentId = 0
  folderDialog.visible = true
}

function handleFolderMove(folder) {
  folderMoveDialog.folderId = folder.id
  folderMoveDialog.folderName = folder.name
  folderMoveDialog.lockVersion = folder.lockVersion
  folderMoveDialog.visible = true
}

async function handleFolderMoveConfirm(targetParentId) {
  try {
    await moveFolderApi(folderMoveDialog.folderId, {
      targetParentId,
      lockVersion: folderMoveDialog.lockVersion
    })
    ElMessage.success('文件夹移动成功')
    folderMoveDialog.visible = false
    await fetchFolderTree()
  } catch {
    // handled by interceptor
  }
}

function handleFolderAcl(folder) {
  folderAclDialog.folderId = folder.id
  folderAclDialog.folderName = folder.name
  folderAclDialog.folder = folder
  folderAclDialog.visible = true
}

async function handleFolderDelete(folder) {
  try {
    await ElMessageBox.confirm(
      `确定要删除文件夹「${folder.name}」吗？文件夹必须为空才能删除。`,
      '删除确认',
      { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
    )
    await deleteFolder(folder.id)
    ElMessage.success('文件夹已删除')
    if (selectedFolderId.value === folder.id) {
      selectedFolderId.value = null
      selectedFolder.value = null
      selectedAssetId.value = null
      selectedAsset.value = null
      versionList.value = []
    }
    await fetchFolderTree()
    await fetchAssetList()
  } catch {
    // cancelled or handled by interceptor
  }
}

// ========== 素材操作 ==========

function handleAssetEdit(asset) {
  assetEditDialog.asset = asset
  assetEditDialog.visible = true
}

async function handleAssetDelete(asset) {
  try {
    await ElMessageBox.confirm(
      `确定要删除素材「${asset.name}」吗？删除后素材将不再在列表中展示。`,
      '删除确认',
      { confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning' }
    )
    await deleteAsset(asset.id)
    ElMessage.success('素材已删除')
    handleDetailClose()
    await fetchAssetList()
  } catch {
    // 取消或错误
  }
}

function handleAssetMove(asset) {
  assetMoveDialog.assetId = asset.id
  assetMoveDialog.assetName = asset.name
  assetMoveDialog.lockVersion = asset.lockVersion
  assetMoveDialog.visible = true
}

async function handleAssetMoveConfirm(targetFolderId) {
  try {
    await moveAssetApi(assetMoveDialog.assetId, {
      targetFolderId,
      lockVersion: assetMoveDialog.lockVersion
    })
    ElMessage.success('素材移动成功')
    assetMoveDialog.visible = false
    handleDetailClose()
    await fetchAssetList()
  } catch {
    // handled by interceptor
  }
}

function handleAssetTagsEdit(asset) {
  assetTagsDialog.asset = asset
  assetTagsDialog.visible = true
}

function handleNewVersion(asset) {
  if (asset.type === 'TEXT') {
    textVersionDialog.asset = asset
    textVersionDialog.visible = true
    return
  }
  newVersionDialog.assetId = asset.id
  newVersionDialog.visible = true
}

async function handleRestoreVersion({ assetId, versionId }) {
  try {
    await ElMessageBox.confirm(
      '恢复历史版本将创建一个新版本，复制历史版本内容并设为当前版本。确定恢复吗？',
      '恢复版本',
      { confirmButtonText: '确定', cancelButtonText: '取消', type: 'info' }
    )
    await restoreVersionApi(assetId, versionId, {})
    ElMessage.success('版本已恢复')
    await fetchAssetDetail()
  } catch {
    // 取消或错误
  }
}

function resetVersionDialogs() {
  newVersionDialog.visible = false
  newVersionDialog.assetId = null
  textVersionDialog.visible = false
  textVersionDialog.asset = null
}

// ========== 成功回调 ==========

function onUploadSuccess() {
  showUploadDialog.value = false
  fetchAssetList()
}

function onCreateSuccess() {
  showTextCreateDialog.value = false
  fetchAssetList()
}

function onAssetUpdateSuccess() {
  assetEditDialog.visible = false
  assetTagsDialog.visible = false
  fetchAssetDetail()
  fetchAssetList()
}

function onVersionUploadSuccess() {
  resetVersionDialogs()
  fetchAssetDetail()
  fetchAssetList()
}

// ========== 初始化 ==========

onMounted(() => {
  fetchFolderTree()
  fetchTags()
})
</script>

<style scoped>
.asset-repository {
  height: 100%;
}

.toolbar :deep(.el-input__wrapper),
.toolbar :deep(.el-select .el-input__wrapper) {
  box-shadow: none;
  background-color: #f8fafc;
}

.toolbar :deep(.el-input__wrapper:hover),
.toolbar :deep(.el-select .el-input__wrapper:hover) {
  box-shadow: 0 0 0 1px #cbd5e1 inset;
}
</style>
